/**
 * System Integration Schema Validator
 * Validates system integration data against the API schema requirements
 */

import { validateIntegrationServiceSchema } from './service-integration-validator.js'

/**
 * Validates a system integration object against the API schema
 * @param {Object} data - The system integration data to validate
 * @throws {Error} - Throws an error if validation fails
 */
export function validateSystemIntegrationSchema (data) {
	// Required field validation
	if (!data.integrationSystemName) {
		throw new Error('integrationSystemName is required')
	}

	// String length validation for integrationSystemName
	if (typeof data.integrationSystemName !== 'string' ||
		data.integrationSystemName.length < 1 ||
		data.integrationSystemName.length > 100) {
		throw new Error('integrationSystemName must be between 1 and 100 characters')
	}

	// Integer validation with minimum constraints
	if (data.authenticationTypeId !== undefined && data.authenticationTypeId !== null) {
		if (!Number.isInteger(data.authenticationTypeId) || data.authenticationTypeId <= 0) {
			throw new Error('authenticationTypeId must be a positive integer')
		}
	}

	if (data.brandId !== undefined && data.brandId !== null) {
		if (!Number.isInteger(data.brandId)) {
			throw new Error('brandId must be an integer')
		}
	}

	if (data.frequencyInSeconds !== undefined && data.frequencyInSeconds !== null) {
		if (!Number.isInteger(data.frequencyInSeconds) || data.frequencyInSeconds < 0) {
			throw new Error('frequencyInSeconds must be a non-negative integer')
		}
	}

	// Boolean validation
	if (data.enabled !== undefined && data.enabled !== null) {
		if (typeof data.enabled !== 'boolean') {
			throw new Error('enabled must be a boolean value')
		}
	}

	// DateTime validation
	if (data.validFrom !== undefined && data.validFrom !== null) {
		if (!isValidDateTime(data.validFrom)) {
			throw new Error('validFrom must be a valid ISO 8601 datetime')
		}
	}

	if (data.validTo !== undefined && data.validTo !== null) {
		if (!isValidDateTime(data.validTo)) {
			throw new Error('validTo must be a valid ISO 8601 datetime')
		}
	}

	// UUID validation for templateGUID
	if (data.templateGUID !== undefined && data.templateGUID !== null) {
		if (!isValidUUID(data.templateGUID)) {
			throw new Error('templateGUID must be a valid UUID')
		}
	}

	// String fields can be null but if provided must be strings
	const stringFields = [
		'endpointAddress', 'clientId', 'clientSecret', 'username', 'password',
		'token', 'notes', 'auth2Scope', 'authenticationUrl', 'jwtCertKey', 'resource'
	]

	stringFields.forEach(field => {
		if (data[field] !== undefined && data[field] !== null && typeof data[field] !== 'string') {
			throw new Error(`${field} must be a string`)
		}
	})
}

/**
 * Validates if a string is a valid ISO 8601 datetime
 * @param {string} dateString - The datetime string to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidDateTime (dateString) {
	if (typeof dateString !== 'string') return false
	const date = new Date(dateString)
	return !isNaN(date.getTime()) && dateString.includes('T')
}

/**
 * Validates if a string is a valid UUID
 * @param {string} uuid - The UUID string to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidUUID (uuid) {
	if (typeof uuid !== 'string') return false
	const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
	return uuidRegex.test(uuid)
}

/**
 * Validates import data for system integration and integration services
 * @param {string} jsonData - The JSON string to validate
 * @returns {Object} - Returns { isValid: boolean, error: string|null, data: Object|null }
 */
export function validateImportData (jsonData) {
	if (!jsonData.trim()) {
		return { isValid: false, error: null, data: null }
	}

	try {
		const parsedData = JSON.parse(jsonData)

		// Basic validation for system configuration structure
		if (!parsedData || typeof parsedData !== 'object') {
			throw new Error('Configuration must be a valid JSON object')
		}

		// Check if this is the new export format with systemIntegration and integrationServices
		if (parsedData.systemIntegration) {
			// Validate the system integration
			validateSystemIntegrationSchema(parsedData.systemIntegration)

			// Validate integration services if present
			if (parsedData.integrationServices) {
				if (!Array.isArray(parsedData.integrationServices)) {
					throw new Error('integrationServices must be an array')
				}

				// Validate each integration service using the imported validator
				for (let i = 0; i < parsedData.integrationServices.length; i++) {
					const service = parsedData.integrationServices[i]
					try {
						validateIntegrationServiceSchema(service)
					} catch (serviceError) {
						throw new Error(`Integration ${i + 1} (${service.name || 'unnamed'}): ${serviceError.message}`)
					}
				}
			}
		} else {
			// Legacy format - assume it's a direct system integration object
			validateSystemIntegrationSchema(parsedData)
		}

		return { isValid: true, error: null, data: parsedData }
	} catch (error) {
		return {
			isValid: false,
			error: error.message || 'Invalid JSON format',
			data: null
		}
	}
}
